import { useState } from 'react';
import {
  AuditOutlined,
  ColumnWidthOutlined,
  DeleteOutlined,
  FilterOutlined,
  FlagOutlined,
  MessageOutlined,
  ReloadOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  Empty,
  Flex,
  Grid,
  Input,
  List,
  Modal,
  Pagination,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router';
import { useEstadoDeLista } from '@titularis/ui';
import type { EstadoDoItem, ItemDaListaDeComentario } from '@titularis/api-client';
import { api } from '../../api';
import {
  CabecalhoRedimensionavel,
  useLarguraDasColunas,
} from '../../components/colunasRedimensionaveis';
import { ESTADO_DA_LISTA_COR, ESTADO_DA_LISTA_LABEL, formatarData } from '../../dominio';
import {
  lerConsulta,
  MOTORES,
  opcoesDeEstado,
  ORDENS,
  paginaDaLista,
  POR_PAGINA_PADRAO,
  quantosFiltros,
  resumoDaLista,
  TAMANHOS_DE_PAGINA,
  type RecorteDaLista,
} from './listaDoComentario';

/**
 * A lista de trabalho do Comentário Assistido.
 *
 * **Ela existe por causa do item que trava.** A questão nasce `pendente` e só
 * sai quando o Claude Code pede trabalho pelo MCP (D-24). Mas ele roda na
 * máquina de alguém, e máquina fecha - a questão fica `reivindicada` sem ninguém
 * trabalhando nela, e o motor que a tomou não vai voltar para devolvê-la.
 *
 * Só uma pessoa resolve isso, e para resolver precisa ver. Sem esta tela, o
 * `devolver` existe apenas como ferramenta do motor, e o que trava fica
 * invisível até alguém estranhar que a questão nunca ficou pronta.
 *
 * **A fila passou de dezenas para centenas, e a tela tinha um filtro só.** Com
 * tudo numa página, o item travado ficava a três rolagens de distância de quem
 * veio procurá-lo - a tela mostrava a fila e escondia justamente o que ela
 * existe para mostrar. O desenho agora é o da lista de Questões: recorte na URL,
 * página do servidor e o formato da fila dito em números, para nenhuma pergunta
 * depender de contar linhas com o olho.
 */

const MOTOR_LABEL: Record<string, string> = {
  claude_code: 'Claude Code',
  openrouter: 'Nuvem',
};

/** Como o item aparece no título do modal da conversa. */
const nomeDoItem = (item: ItemDaListaDeComentario): string =>
  item.label ?? (item.position === null ? 'Questão sem número' : `Questão ${item.position}`);

/*
 * Rótulos curtos de propósito: com "Comentário dos itens" por extenso, as três
 * etiquetas ocupavam três linhas e empurravam a data para fora da tabela. O
 * nome inteiro fica no `title` de cada etiqueta.
 */
const ABA_LABEL: Record<string, string> = {
  commentary: 'Itens',
  legislation: 'Legislação',
  jurisprudence: 'Jurisprudência',
  doctrine: 'Doutrina',
};

const ABA_TITULO: Record<string, string> = {
  commentary: 'Comentário de cada alternativa',
  legislation: 'Aba de Legislação',
  jurisprudence: 'Aba de Jurisprudência',
  doctrine: 'Aba de Doutrina',
};

/*
 * As larguras de partida da tabela, que o clique duplo na divisa devolve. Todas
 * as colunas medidas precisam de uma - ver `colunasRedimensionaveis.tsx`.
 */
const LARGURA_PADRAO: Record<string, number> = {
  // Medidas na tela, e não estimadas: a Questão precisa dos dois textos - o
  // número e o nome da prova - sem cortar o ano, que é o que distingue duas
  // edições do mesmo exame. A Situação cabe em menos porque as duas linhas dela
  // são curtas e de tamanho previsível.
  questao: 260,
  estado: 130,
  conversa: 120,
  abas: 220,
  situacao: 250,
};

/*
 * A coluna de ações não é medida pelo arrasto - ela é presa à direita e tem
 * conteúdo fixo -, mas ocupa espaço: fora da conta da rolagem, a última coluna
 * perde o fim.
 */
const LARGURA_DAS_ACOES = 180;

export function ComentarioAssistidoPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const telas = Grid.useBreakpoint();
  // O mesmo corte das demais listagens do admin: abaixo de `md`, a tabela de
  // seis colunas não cabe, e rolar na horizontal esconde justamente as ações.
  const compacto = !telas.md;
  /*
   * O `Segmented` é o desenho certo para o estado - as seis opções lado a lado
   * dizem, de uma vez, o formato da fila -, mas com as contagens ele passa de
   * 700px. Abaixo de `lg` ele viraria uma fila cortada na borda, e "Falharam" -
   * justamente a que se procura quando algo deu errado - seria a que sai. O
   * `Select` mostra as seis.
   */
  const estadoEmLista = !telas.lg;
  const { filtrar, irParaPagina, busca } = useEstadoDeLista();
  const [gavetaAberta, setGavetaAberta] = useState(false);

  /**
   * O item cuja conversa está aberta no modal, ou nulo.
   *
   * Guarda o item inteiro, e não o id: a lista se atualiza sozinha a cada dez
   * segundos enquanto houver trabalho em curso, e procurar o id numa lista que
   * acabou de ser trocada faria o modal piscar vazio no meio da leitura.
   */
  const [conversaAberta, setConversaAberta] = useState<ItemDaListaDeComentario | null>(null);

  /*
   * Recorte, ordem e página moram na URL: a tela sobrevive a sair e voltar, a
   * recarregar e a ser mandada por link - e o estado escolhido no `Segmented`
   * deixa de morrer a cada visita. Ver `estado-de-lista.ts`.
   */
  const consulta = lerConsulta(new URLSearchParams(busca));
  const { status, ordem, page, porPagina, ...recorte } = consulta;

  /*
   * A atualização automática segue a **fila**, e não a página: com centenas de
   * itens, o que está em trabalho pode estar na página três, e olhar só para o
   * que está na tela desligaria a atualização justamente quando ela importa.
   */
  const resumo = useQuery({
    ...resumoDaLista(consulta),
    refetchInterval: (query) => (query.state.data?.porEstado.reivindicada ? 10_000 : false),
  });
  const emTrabalho = (resumo.data?.porEstado.reivindicada ?? 0) > 0;

  const lista = useQuery({
    ...paginaDaLista(consulta),
    refetchInterval: emTrabalho ? 10_000 : false,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['comment-jobs'] });

  const devolver = useMutation({
    mutationFn: (id: string) => api.devolverComentario(id),
    onSuccess: async () => {
      message.success('Questão devolvida à lista.');
      await invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const desenfileirar = useMutation({
    mutationFn: (id: string) => api.desenfileirarComentario(id),
    onSuccess: async () => {
      message.success('Item removido da lista.');
      await invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const itens = lista.data?.items ?? [];
  const travadas = resumo.data?.travadas ?? 0;
  const quantosRecortes = quantosFiltros(recorte);

  /*
   * A busca é controlada, e o campo acompanha a URL.
   *
   * Com `defaultValue`, "Limpar" apagava o termo da consulta e deixava a palavra
   * escrita no campo: a tela mostrava a fila inteira dizendo estar filtrada. É o
   * mesmo ajuste de estado durante a renderização que a lista de Questões faz
   * com o rascunho dos filtros - o React refaz a renderização com o valor novo,
   * sem passar pela tela intermediária errada.
   */
  const [digitado, setDigitado] = useState(recorte.termo);
  const [termoDaUrl, setTermoDaUrl] = useState(recorte.termo);
  if (recorte.termo !== termoDaUrl) {
    setTermoDaUrl(recorte.termo);
    setDigitado(recorte.termo);
  }

  /**
   * Muda o recorte e volta para a primeira página, que é o que `filtrar` faz.
   *
   * Só o que vem nas mudanças é escrito: o que não está aqui continua na URL,
   * então o campo mexido não precisa carregar os outros três junto - e não há
   * como um deles ser esquecido no caminho.
   */
  const recortar = ({ paradas, ...resto }: Partial<RecorteDaLista>) =>
    filtrar({
      ...resto,
      ...(paradas === undefined ? {} : { paradas: paradas ? '1' : undefined }),
    });

  /** O recorte inteiro apagado, com o estado à parte: nem sempre se limpa os dois. */
  const RECORTE_VAZIO = {
    examId: undefined,
    motor: undefined,
    paradas: undefined,
    termo: undefined,
  };
  const limpar = () => filtrar(RECORTE_VAZIO);
  // Numa escrita só: dois `filtrar` no mesmo clique disputariam a mesma URL.
  const verAFilaInteira = () => filtrar({ ...RECORTE_VAZIO, status: undefined });

  /**
   * As ações de um item, servindo aos dois desenhos.
   *
   * Extraídas porque a tabela e a lista do celular mostram exatamente as mesmas
   * ações, e duas cópias divergiriam na primeira mudança - com a do celular
   * ficando para trás, que é onde ninguém olha ao testar.
   */
  const acoes = (item: ItemDaListaDeComentario) => (
    <Space size={4} wrap>
      {item.status === 'reivindicada' ? (
        <Popconfirm
          title="Devolver à lista?"
          description="A questão volta a ficar disponível. O que já foi gravado como sugestão continua lá."
          okText="Devolver"
          cancelText="Cancelar"
          onConfirm={() => devolver.mutate(item.id)}
        >
          {/*
            A espera é **daquela linha**, e não da tela: com `isPending` sozinho,
            devolver um item punha as seis linhas em trabalho girando ao mesmo
            tempo, como se todas estivessem sendo devolvidas.
          */}
          <Button
            size="small"
            icon={<UndoOutlined />}
            loading={devolver.isPending && devolver.variables === item.id}
          >
            Devolver
          </Button>
        </Popconfirm>
      ) : null}

      {/*
        Tirar da lista só o que ainda não começou. Com um motor trabalhando,
        apagar a linha o deixaria escrevendo contra um item que não existe mais -
        por isso o caminho é devolver primeiro, e a tela diz isso em vez de
        esconder o botão.
      */}
      <Tooltip
        title={
          item.status === 'reivindicada'
            ? 'Está com um motor. Devolva antes de tirar da lista.'
            : undefined
        }
      >
        <Popconfirm
          title="Tirar da lista?"
          description="A questão sai da lista de trabalho. Nada do que já foi gravado é apagado."
          okText="Tirar"
          cancelText="Cancelar"
          disabled={item.status === 'reivindicada'}
          onConfirm={() => desenfileirar.mutate(item.id)}
        >
          <Button
            size="small"
            icon={<DeleteOutlined />}
            disabled={item.status === 'reivindicada'}
            loading={desenfileirar.isPending && desenfileirar.variables === item.id}
          >
            Tirar
          </Button>
        </Popconfirm>
      </Tooltip>
    </Space>
  );

  /**
   * O que a tentativa custou, colado no nome do motor (G-COST).
   *
   * **Na mesma linha, e não numa coluna nova.** A tabela já tem seis colunas e
   * some abaixo de `md`; a sétima empurraria as ações para fora da tela, que é o
   * que esta tela existe para mostrar. Ao lado do motor o número também se lê
   * melhor: é o preço **daquele** motor, e comparar os dois é metade do motivo
   * de registrá-lo.
   *
   * **Sem custo não se escreve zero.** O item que ninguém trabalhou não custou
   * nada, e o Claude Code sem o gancho de medição instalado não tem como dizer
   * quanto gastou - "US$ 0,00" afirmaria as duas coisas de uma vez.
   */
  const custo = (item: ItemDaListaDeComentario) => {
    if (item.costUsd === null || item.costUsd === 0) return null;
    return (
      <Tooltip title={item.model ?? undefined}>
        <Typography.Text type="secondary"> · US$ {item.costUsd.toFixed(4)}</Typography.Text>
      </Tooltip>
    );
  };

  /**
   * A coluna da Situação empilha duas linhas, e nenhuma delas pode esticar a
   * tabela.
   *
   * **O `Space` é `inline-flex`, e `inline-flex` cresce com o conteúdo.** A
   * mensagem de "esperando acervo" tem uma frase inteira, e com `nowrap` ela
   * levava a célula junto: a rolagem horizontal da tabela ia a **dez mil pixels**
   * por causa de uma linha, prometendo colunas que não existem. A largura de
   * 100% prende o empilhamento à célula, que no `tableLayout: fixed` tem medida
   * própria, e o `overflow` corta o que passar em vez de empurrar.
   */
  const empilhado = { width: '100%', minWidth: 0, overflow: 'hidden' } as const;

  /** Quem está com a questão, e desde quando. Serve aos dois desenhos. */
  const situacao = (item: ItemDaListaDeComentario) => {
    const motor = item.engine ? (MOTOR_LABEL[item.engine] ?? item.engine) : null;

    if (item.status === 'reivindicada') {
      return (
        <Space orientation="vertical" size={0} style={empilhado}>
          <Typography.Text style={{ whiteSpace: 'nowrap' }}>
            {motor ?? 'motor não declarado'}
            {item.claimedByName ? ` · ${item.claimedByName}` : ''}
            {/* Em trabalho o valor é parcial, e mostrá-lo assim mesmo é o ponto:
                é o que deixa ver uma sessão gastando mais do que a questão vale. */}
            {custo(item)}
          </Typography.Text>
          {/*
            **Quem decide o amarelo é o servidor** (`parada`), e não uma cópia da
            régua aqui: é o mesmo cálculo que conta as travadas da fila inteira, e
            duas cópias divergiriam no primeiro ajuste do prazo - a linha em
            amarelo fora do aviso que a resume.
          */}
          <Typography.Text
            type={item.parada ? 'warning' : 'secondary'}
            style={{ fontSize: 12, whiteSpace: 'nowrap' }}
          >
            desde {formatarData(item.claimedAt)}
          </Typography.Text>
        </Space>
      );
    }

    if (item.status === 'pendente') {
      return (
        <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
          desde {formatarData(item.createdAt)}
        </Typography.Text>
      );
    }

    /*
     * **O motivo à vista, e não escondido no histórico de runs.** Este item não
     * está esperando por acaso: um motor tentou, não achou no acervo o que a
     * questão exige, e disse o quê. Sem a frase aqui, quem abre a fila vê um
     * item parado e não tem como saber que o conserto é ingerir uma fonte - que
     * é a única coisa que faz este item andar.
     */
    if (item.status === 'aguardando_acervo') {
      return (
        <Space orientation="vertical" size={0} style={empilhado}>
          <Typography.Text style={{ whiteSpace: 'nowrap' }}>
            {motor ?? 'motor não declarado'}
            {custo(item)}
          </Typography.Text>
          <Tooltip title={item.errorMessage ?? undefined}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {item.errorMessage ?? 'falta material no acervo'}
            </Typography.Text>
          </Tooltip>
        </Space>
      );
    }

    return (
      <Space orientation="vertical" size={0} style={empilhado}>
        <Typography.Text style={{ whiteSpace: 'nowrap' }}>
          {motor ?? '-'}
          {custo(item)}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {item.status === 'feita' ? 'concluída' : 'parou'} em{' '}
          {formatarData(item.finishedAt ?? item.createdAt)}
        </Typography.Text>
      </Space>
    );
  };

  /**
   * O que a linha tem a dizer, **dito na própria linha** (D-41).
   *
   * **A seta de expandir não servia, e o motivo é o que decide este desenho.**
   * Ela até só aparecia onde havia conteúdo, mas um `+` é um sinal mudo: não diz
   * se o que está guardado é a ressalva de quem pediu o retrabalho ou a nota de
   * quem escreveu - e essas duas coisas se leem de formas diferentes. A primeira
   * é instrução, e muda o que a próxima tentativa deve fazer; a segunda é
   * explicação, e muda como julgar o que já foi feito. Para distinguir uma da
   * outra era preciso clicar em cada linha, que é justamente varrer a lista
   * clicando - o contrário de varrer.
   *
   * Etiqueta nomeada resolve sem clique nenhum: quem passa os olhos vê onde há
   * ressalva, onde há nota, e onde há as duas. O texto inteiro fica a um clique,
   * no modal, que é onde 4000 caracteres cabem sem espremer coluna.
   */
  const etiquetasDaConversa = (item: ItemDaListaDeComentario) => {
    if (!item.briefing && !item.engineNote) return null;

    return (
      // A dica diz que dá para abrir. Sem ela a etiqueta parece só um selo, e
      // quem quer ler o texto não descobre que basta clicar.
      <Tooltip title="Ver o texto completo">
        <Button
          type="link"
          size="small"
          // Sem a moldura do botão: o que se vê são as etiquetas, e o botão está
          // ali para dar a elas foco por teclado e um alvo de clique honesto.
          style={{ padding: 0, height: 'auto' }}
          onClick={() => setConversaAberta(item)}
        >
          <Space size={4} wrap>
            {item.briefing ? (
              <Tag color="purple" icon={<FlagOutlined />} style={{ marginInlineEnd: 0 }}>
                Ressalva
              </Tag>
            ) : null}
            {item.engineNote ? (
              <Tag color="cyan" icon={<MessageOutlined />} style={{ marginInlineEnd: 0 }}>
                Nota
              </Tag>
            ) : null}
          </Space>
        </Button>
      </Tooltip>
    );
  };

  /** O texto inteiro das duas, no modal. */
  const blocoDaConversa = (rotulo: string, texto: string) => (
    <div>
      <Typography.Text strong>{rotulo}</Typography.Text>
      <Typography.Paragraph
        style={{
          marginBottom: 0,
          // A nota costuma vir em frases; preservar a quebra mantém o que o
          // motor escreveu legível em vez de virar um bloco só.
          whiteSpace: 'pre-wrap',
        }}
      >
        {texto}
      </Typography.Paragraph>
    </div>
  );

  const vazio = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        quantosRecortes > 0 || status
          ? 'Nenhum item com esse recorte.'
          : 'Nada na lista. Enfileire questões a partir de uma prova.'
      }
    >
      {quantosRecortes > 0 || status ? (
        <Button onClick={verAFilaInteira}>Ver a fila inteira</Button>
      ) : null}
    </Empty>
  );

  /**
   * No celular, cartão em vez de linha.
   *
   * A tabela tem seis colunas, e num aparelho estreito rolar na horizontal
   * esconderia justamente as ações - que são a razão de a tela existir. O cartão
   * põe tudo na vertical e mantém devolver e tirar sempre à vista.
   */
  const listaNoCelular = (
    <List
      loading={lista.isPending}
      dataSource={itens}
      locale={{ emptyText: vazio }}
      renderItem={(item) => (
        <List.Item style={{ paddingInline: 0, alignItems: 'flex-start' }}>
          {/*
            `minWidth: 0` é o que faz o corte funcionar: sem ele o filho cresce
            com o texto e o cartão vaza para fora da tela em vez de truncar.
          */}
          <Flex vertical gap={6} style={{ width: '100%', minWidth: 0 }}>
            <Flex align="center" gap={8} wrap>
              <Tag color={ESTADO_DA_LISTA_COR[item.status]} style={{ marginInlineEnd: 0 }}>
                {ESTADO_DA_LISTA_LABEL[item.status]}
              </Tag>
              <Typography.Link onClick={() => void navigate(`/questoes/${item.questionId}`)}>
                {item.label ?? (item.position === null ? 'Sem número' : `Questão ${item.position}`)}
              </Typography.Link>
            </Flex>

            <Typography.Text
              type="secondary"
              style={{ fontSize: 12 }}
              ellipsis={{ tooltip: item.examName ?? undefined }}
            >
              {item.examName ?? 'Prova removida'}
            </Typography.Text>

            {item.errorMessage ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {item.errorMessage}
              </Typography.Text>
            ) : null}

            <Space size={4} wrap>
              {item.requestedFields.map((campo) => (
                <Tag key={campo} style={{ marginInlineEnd: 0 }}>
                  {ABA_LABEL[campo] ?? campo}
                </Tag>
              ))}
            </Space>

            <div>{situacao(item)}</div>
            {/*
              As mesmas etiquetas do desktop, e o mesmo modal. No celular o
              ganho é maior: o texto inline empurrava as ações para fora da tela,
              e a etiqueta diz o que existe sem custar altura nenhuma.

              Renderizado só quando há conversa - o pai é um `Flex` com `gap`, e
              uma `div` vazia somaria espaço em todo cartão sem ressalva e sem
              nota, que é a maioria deles.
            */}
            {item.briefing || item.engineNote ? <div>{etiquetasDaConversa(item)}</div> : null}
            <div>{acoes(item)}</div>
          </Flex>
        </List.Item>
      )}
    />
  );

  const colunas = useLarguraDasColunas('titularis:comentarios:colunas', LARGURA_PADRAO);

  const tabela = (
    <Table<ItemDaListaDeComentario>
      rowKey="id"
      size="small"
      loading={lista.isPending}
      dataSource={itens}
      /*
       * A soma das larguras, em número. Ver `colunasRedimensionaveis.tsx`: é o
       * que faz encolher uma coluna cortar o texto em vez de ser ignorado, e o
       * que faz arrastar alargar a tabela em vez de espremer a vizinha. As ações
       * não são medidas pelo arrasto, mas ocupam espaço - fora da conta, a
       * última coluna perde o fim.
       */
      scroll={{ x: colunas.total + LARGURA_DAS_ACOES }}
      /*
       * Explícito, e não herdado do `ellipsis` de uma coluna qualquer. O Antd
       * liga o layout fixo sozinho quando alguma coluna tem reticência, mas é
       * dele que o arrasto depende para valer.
       */
      tableLayout="fixed"
      components={{ header: { cell: CabecalhoRedimensionavel } }}
      locale={{ emptyText: vazio }}
      pagination={false}
      columns={[
        {
          title: 'Questão',
          key: 'questao',
          dataIndex: 'position',
          ...colunas.coluna('questao'),
          render: (position: number | null, item) => (
            // Mesmo motivo da Situação: o nome da prova é longo, e sem prender o
            // empilhamento à célula ele estica a tabela em vez de ser cortado.
            <Space orientation="vertical" size={0} style={empilhado}>
              <Typography.Link onClick={() => void navigate(`/questoes/${item.questionId}`)}>
                {item.label ?? (position === null ? 'Sem número' : `Questão ${position}`)}
              </Typography.Link>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12 }}
                // O nome da prova quebrava em duas linhas e dobrava a altura de
                // toda linha da tabela; o título inteiro fica no `title`.
                ellipsis={{ tooltip: item.examName ?? undefined }}
              >
                {item.examName ?? 'Prova removida'}
              </Typography.Text>
            </Space>
          ),
        },
        {
          title: 'Estado',
          key: 'estado',
          dataIndex: 'status',
          ...colunas.coluna('estado'),
          render: (estado: EstadoDoItem, item) => (
            <Space orientation="vertical" size={2}>
              <Tag color={ESTADO_DA_LISTA_COR[estado]} style={{ marginInlineEnd: 0 }}>
                {ESTADO_DA_LISTA_LABEL[estado]}
              </Tag>
              {item.errorMessage ? (
                <Tooltip title={item.errorMessage}>
                  <Typography.Text type="danger" style={{ fontSize: 12, cursor: 'help' }}>
                    ver o erro
                  </Typography.Text>
                </Tooltip>
              ) : null}
            </Space>
          ),
        },
        {
          /*
           * **Coluna própria, e com cabeçalho** (D-41). A etiqueta precisa de
           * um nome em cima dela para ser aprendida uma vez e reconhecida
           * depois; enfiada na Situação ou no Estado, "Ressalva" seria mais um
           * enfeite colorido entre outros e voltaria a exigir clique para
           * saber o que é.
           */
          title: 'Conversa',
          key: 'conversa',
          ...colunas.coluna('conversa'),
          render: (_valor, item) => etiquetasDaConversa(item),
        },
        {
          title: 'Abas pedidas',
          key: 'abas',
          dataIndex: 'requestedFields',
          ...colunas.coluna('abas'),
          render: (campos: string[]) => (
            <Space size={4} wrap>
              {campos.map((campo) => (
                <Tooltip key={campo} title={ABA_TITULO[campo] ?? campo}>
                  <Tag style={{ marginInlineEnd: 0 }}>{ABA_LABEL[campo] ?? campo}</Tag>
                </Tooltip>
              ))}
            </Space>
          ),
        },
        {
          /*
           * **Uma coluna, e não duas.** "Com quem" e "Entrou" separadas
           * somavam mais largura do que a tabela tem, e a data saía cortada
           * pela coluna de ações presa à direita - data pela metade parece
           * defeito mesmo quando basta rolar. Juntas, cada estado mostra o
           * carimbo que importa nele: quem espera mostra desde quando espera,
           * e quem está em trabalho mostra desde quando está.
           */
          title: 'Situação',
          key: 'situacao',
          dataIndex: 'claimedByName',
          ...colunas.coluna('situacao'),
          render: (_nome: string | null, item) => situacao(item),
        },
        {
          title: '',
          key: 'acoes',
          width: LARGURA_DAS_ACOES,
          /*
           * Presa à direita porque é a razão de a tela existir. Com a tabela
           * rolando na horizontal, devolver e tirar saíam do campo de visão -
           * e a ação que resolve o item travado ficava atrás de uma rolagem
           * que ninguém adivinha que precisa fazer.
           */
          fixed: 'right',
          render: (_valor, item) => acoes(item),
        },
      ]}
    />
  );

  /**
   * O recorte da fila: prova, motor e o corte dos parados.
   *
   * **Aplicam na hora, sem botão de "Buscar".** São três, e não os oito da lista
   * de Questões: ali o rascunho existe porque montar o filtro custava uma
   * consulta por campo mexido; aqui cada escolha é a consulta inteira.
   */
  const recortes = (
    <Flex gap={12} wrap vertical={compacto}>
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        /*
         * A lista abre pelo tamanho do nome, e não pelo da caixa.
         *
         * Os nomes das provas só se distinguem no **fim** - "1º Exame Nacional
         * dos Cartórios - Enac, 2024.1" contra o mesmo com 2026.1 -, e presa à
         * largura da caixa a lista mostrava três opções idênticas, todas
         * cortadas antes da edição. Escolher entre elas era adivinhar.
         */
        popupMatchSelectWidth={false}
        placeholder="Prova"
        style={compacto ? { width: '100%' } : { minWidth: 260, flex: 1 }}
        value={recorte.examId}
        loading={resumo.isPending}
        onChange={(valor: string | undefined) => recortar({ examId: valor })}
        /*
         * Só as provas que **têm** item na fila. Uma caixa com todas as provas
         * cadastradas ofereceria dezenas de escolhas que devolvem lista vazia -
         * opção que sempre devolve nada é promessa quebrada.
         */
        options={(resumo.data?.provas ?? []).map((prova) => ({
          value: prova.id,
          label: prova.name,
        }))}
        notFoundContent="Nenhuma prova com item na fila."
      />
      <Select
        allowClear
        placeholder="Motor"
        style={compacto ? { width: '100%' } : { width: 180 }}
        value={recorte.motor}
        onChange={(valor) => recortar({ motor: valor })}
        options={MOTORES.map((motor) => ({ value: motor, label: MOTOR_LABEL[motor] }))}
      />
      <Tooltip title="Só o que está com um motor há tempo demais - é a mesma conta do aviso do topo.">
        <Checkbox
          checked={recorte.paradas}
          onChange={(evento) => recortar({ paradas: evento.target.checked })}
        >
          Só as paradas
        </Checkbox>
      </Tooltip>
    </Flex>
  );

  const seletorDeOrdem = (
    <Select
      style={compacto ? { flex: 1, minWidth: 150 } : { width: 220 }}
      value={ordem ?? 'recentes'}
      onChange={(valor) => filtrar({ ordem: valor === 'recentes' ? undefined : valor })}
      options={ORDENS}
    />
  );

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      {/*
        No celular o título e os botões empilham. Lado a lado com 390px,
        "Sugestões para revisar" espremia o título até ele quebrar em três linhas.
      */}
      <Flex
        align={compacto ? 'stretch' : 'start'}
        justify="space-between"
        gap={12}
        vertical={compacto}
      >
        <div style={{ minWidth: 0 }}>
          <Typography.Title level={compacto ? 4 : 3} style={{ marginBottom: 4 }}>
            Comentário Assistido
          </Typography.Title>
          <Typography.Text type="secondary">
            As questões na lista de trabalho, e quem está com cada uma.
          </Typography.Text>
        </div>
        <Space size={8} wrap>
          {/*
            O outro lado da lista: o que já foi escrito e espera gente.
            Esta tela mostra o trabalho **em curso** - o que está pendente, o que
            um motor tomou, o que travou. O item que fica pronto simplesmente sai
            dela, e o comentário recém-escrito ficava sem porta de saída: quem
            acabou de mandar vinte questões para a nuvem tinha de ir até Questões
            e montar o filtro na mão para achar o que revisar.

            O filtro é o de responsável, e não um endereço próprio: "Comentário
            Assistido" ali significa exatamente "escreveu e ninguém assumiu".
          */}
          <Button
            icon={<AuditOutlined />}
            block={compacto}
            onClick={() => void navigate('/questoes?responsavel=/comentario-assistido')}
          >
            Sugestões para revisar
          </Button>
          <Button
            icon={<ReloadOutlined />}
            block={compacto}
            loading={lista.isFetching || resumo.isFetching}
            onClick={() => {
              void lista.refetch();
              void resumo.refetch();
            }}
          >
            Atualizar
          </Button>
        </Space>
      </Flex>

      {/*
        O aviso conta a **fila inteira**, e não a página: com centenas de itens,
        contar o que está na tela chamaria de zero uma fila travada na página
        três - e o item abandonado é a razão de a tela existir.
      */}
      {travadas > 0 ? (
        <Alert
          type="warning"
          showIcon
          title={`${travadas} ${travadas === 1 ? 'questão está' : 'questões estão'} em trabalho há tempo demais`}
          description="O motor que a tomou pode ter parado no meio. Devolver a coloca de volta na lista, sem perder o que já foi gravado."
          action={
            // Leva direto para elas: sem isto o aviso diz que há um problema e
            // deixa a busca por conta de quem lê.
            recorte.paradas ? null : (
              <Button size="small" onClick={() => recortar({ paradas: true })}>
                Ver as paradas
              </Button>
            )
          }
        />
      ) : null}

      {lista.error ? <Alert type="error" showIcon title={(lista.error as Error).message} /> : null}

      {/*
        A busca fica fora do recorte da gaveta: é o caminho principal para achar
        uma questão, não mais um corte. No celular, dentro da gaveta, procurar
        exigiria abrir, digitar, aplicar e fechar.
      */}
      <Input.Search
        allowClear
        size="large"
        enterButton
        value={digitado}
        onChange={(evento) => setDigitado(evento.target.value)}
        placeholder="Número da questão ou nome da prova"
        onSearch={(valor) => recortar({ termo: valor.trim() })}
      />

      {/*
        O estado, com quanto há em cada um.
        **O número é metade da informação.** Sem ele, escolher "Esperando acervo"
        para descobrir que não há nenhum é uma ida e volta por nada - e a fila
        com centenas de itens torna isso a pergunta mais comum da tela.
      */}
      {estadoEmLista ? (
        <Select<EstadoDoItem | 'todos'>
          value={status ?? 'todos'}
          onChange={(valor) => filtrar({ status: valor === 'todos' ? undefined : valor })}
          style={{ width: '100%' }}
          options={opcoesDeEstado(resumo.data?.porEstado).map((opcao) => ({
            value: opcao.value,
            label: opcao.count === undefined ? opcao.label : `${opcao.label} (${opcao.count})`,
          }))}
        />
      ) : (
        <Segmented<EstadoDoItem | 'todos'>
          block
          value={status ?? 'todos'}
          onChange={(valor) => filtrar({ status: valor === 'todos' ? undefined : valor })}
          options={opcoesDeEstado(resumo.data?.porEstado).map((opcao) => ({
            value: opcao.value,
            /*
             * O número **embaixo** do rótulo, e não ao lado.
             *
             * Com `block`, os seis pedaços dividem a largura por igual, e o que
             * decide o tamanho de todos é o rótulo mais longo. Lado a lado,
             * "Esperando acervo 312" pede uns 160px por pedaço - quase mil no
             * total, mais do que sobra num monitor de 1200. Empilhado, o pedaço
             * volta a caber, e o que sobra de espaço vira altura em vez de
             * rolagem horizontal.
             *
             * E se ainda assim faltar, quem corta é o rótulo: o número, que é a
             * razão de ele estar aqui, fica inteiro.
             */
            label: (
              <Flex vertical align="center" style={{ minWidth: 0, lineHeight: 1.35 }}>
                <span
                  style={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {opcao.label}
                </span>
                {opcao.count === undefined ? null : (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {opcao.count}
                  </Typography.Text>
                )}
              </Flex>
            ),
            // O socorro do rótulo cortado, que o Antd oferece pronto no item.
            tooltip: opcao.label,
          }))}
        />
      )}

      <Card size="small">
        {/*
          No desktop o recorte fica aberto; no celular ele mora numa gaveta, como
          na lista de Questões. Três campos empilhados empurrariam a lista para
          baixo da dobra - a tela abriria mostrando o formulário em vez do
          resultado.
        */}
        {compacto ? (
          <Flex gap={8} align="center" wrap style={{ marginBottom: 12 }}>
            <Badge count={quantosRecortes}>
              <Button icon={<FilterOutlined />} onClick={() => setGavetaAberta(true)}>
                Filtros
              </Button>
            </Badge>
            {seletorDeOrdem}
            <Drawer
              title="Filtrar a lista"
              placement="bottom"
              size="auto"
              open={gavetaAberta}
              onClose={() => setGavetaAberta(false)}
            >
              <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                {recortes}
                <Flex justify="flex-end" gap={8}>
                  {quantosRecortes > 0 ? <Button onClick={limpar}>Limpar</Button> : null}
                  <Button type="primary" onClick={() => setGavetaAberta(false)}>
                    Ver a lista
                  </Button>
                </Flex>
              </Space>
            </Drawer>
          </Flex>
        ) : (
          <Flex align="center" justify="space-between" gap={12} wrap style={{ marginBottom: 12 }}>
            {recortes}
            <Space size={8}>
              {quantosRecortes > 0 ? (
                <Button type="text" onClick={limpar}>
                  Limpar
                </Button>
              ) : null}
              {seletorDeOrdem}
            </Space>
          </Flex>
        )}

        {/*
          A saída para quem se perdeu nas larguras. Só aparece se houver o que
          desfazer: um botão permanente sugeriria que a tabela vem torta de
          fábrica. No celular não existe, porque lá não há tabela.
        */}
        {!compacto && colunas.personalizada ? (
          <Flex justify="flex-end" style={{ marginBottom: 8 }}>
            <Button
              size="small"
              type="text"
              icon={<ColumnWidthOutlined />}
              onClick={colunas.restaurar}
            >
              Restaurar larguras
            </Button>
          </Flex>
        ) : null}

        {compacto ? listaNoCelular : tabela}

        {/*
          A paginação sai de dentro da tabela para servir aos dois desenhos.

          Sem `hideOnSinglePage`: escondê-la levaria junto o seletor de tamanho,
          e quem tivesse posto 100 por página numa lista de 30 perderia o
          controle para voltar a 20 - só editando a URL na mão. (No celular quem
          esconde o seletor é o próprio Antd, abaixo de 768px, e ali é decisão
          dele: a linha inteira não caberia em 375px.)
        */}
        <Flex justify={compacto ? 'center' : 'flex-end'} style={{ marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={lista.data?.pageSize ?? porPagina}
            total={lista.data?.total ?? 0}
            size={compacto ? 'small' : undefined}
            showSizeChanger
            pageSizeOptions={TAMANHOS_DE_PAGINA}
            showTotal={(total) => `${total} ite${total === 1 ? 'm' : 'ns'}`}
            /*
             * Trocar de página e trocar o tamanho chegam os dois por aqui, e
             * é de propósito que `onShowSizeChange` não seja usado junto: na
             * troca de tamanho o Ant Design dispara os dois callbacks, cada um
             * escreveria na URL na mesma renderização, e a segunda escrita -
             * que só conhece a página - apagaria o tamanho novo. Uma escrita
             * por interação.
             */
            onChange={(proxima, tamanho) => {
              if (tamanho !== porPagina) {
                // Mudar o tamanho muda quais itens cabem na página, então a
                // lista recomeça: continuar na página 7 mostraria outro pedaço.
                filtrar({ porPagina: tamanho === POR_PAGINA_PADRAO ? undefined : tamanho });
                return;
              }
              irParaPagina(proxima);
            }}
          />
        </Flex>
      </Card>

      {/*
        O texto inteiro, fora da grade.

        **Modal, e não Popover ou Tooltip**: são até 4000 caracteres nas duas
        somadas. Tooltip some ao tirar o mouse e não deixa selecionar o texto;
        Popover ancorado a uma célula de tabela que rola na horizontal briga com
        o próprio posicionamento. O modal tem largura própria, rola quando
        precisa, fecha no Esc e não mexe em altura de linha nenhuma.

        `destroyOnHidden` para o corpo não ficar montado com a nota anterior
        enquanto a lista se atualiza sozinha por trás.
      */}
      <Modal
        open={conversaAberta !== null}
        title={conversaAberta ? `Conversa · ${nomeDoItem(conversaAberta)}` : undefined}
        onCancel={() => setConversaAberta(null)}
        footer={null}
        destroyOnHidden
        width={640}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          {conversaAberta?.briefing
            ? blocoDaConversa('Ressalva de quem revisou', conversaAberta.briefing)
            : null}
          {conversaAberta?.engineNote
            ? blocoDaConversa('Nota do motor', conversaAberta.engineNote)
            : null}
        </Space>
      </Modal>
    </Space>
  );
}
